import { readPixels, releaseCanvas, type CanvasLike } from './canvas';
import { detectDocumentCorners } from './edges';
import { enhance, rectify, rotate, scaleToFit } from './operations';
import { scaleCorners, type Corners } from './geometry';
import type { ColourMode, Enhancements } from './filters';

/** Corner detection works on a copy whose long edge is at most this. */
export const DETECT_LONG_EDGE = 800;

export interface RenderParams {
  readonly corners: Corners;
  /** Fine rotation in degrees, applied after the perspective correction. */
  readonly rotation: number;
  readonly settings: Enhancements;
  /** Colour treatment after the corrections; colour when absent. */
  readonly mode?: ColourMode;
}

/**
 * straighten → rotate → enhance, on one source image. The same code runs in the engine's Worker
 * and, where a Worker cannot draw, on the page. Previews and exports render at the same full size:
 * the filters' kernels are sized in pixels, so a smaller preview would not look like the export.
 * Every intermediate canvas is released as soon as the next step has read it, so a render holds
 * at most two full-size canvases at a time.
 */
export class Pipeline {
  private source: CanvasLike | null = null;
  private detectCopy: CanvasLike | null = null;
  private detectFactor = 1;
  private ownsSource = false;

  /** `owned`: the pipeline may release `source` when it is replaced or cleared. */
  setSource(source: CanvasLike, owned: boolean): void {
    this.clear();
    this.source = source;
    this.ownsSource = owned;
  }

  render(params: RenderParams): CanvasLike {
    if (this.source === null) throw new Error('No source image');
    const rectified = rectify(this.source, params.corners);
    const rotated = rotate(rectified, params.rotation);
    if (rotated !== rectified) releaseCanvas(rectified);
    const enhanced = enhance(rotated, params.settings, params.mode);
    releaseCanvas(rotated);
    return enhanced;
  }

  /** Proposed document corners in source coordinates, or null (see `detectDocumentCorners`). */
  detectCorners(): Corners | null {
    if (this.source === null) return null;
    if (this.detectCopy === null) {
      const { canvas, factor } = scaleToFit(this.source, DETECT_LONG_EDGE);
      this.detectCopy = canvas;
      this.detectFactor = factor;
    }
    const found = detectDocumentCorners(readPixels(this.detectCopy));
    return found === null ? null : scaleCorners(found, 1 / this.detectFactor);
  }

  clear(): void {
    if (this.detectCopy !== this.source) releaseCanvas(this.detectCopy);
    if (this.ownsSource) releaseCanvas(this.source);
    this.source = null;
    this.detectCopy = null;
    this.detectFactor = 1;
  }
}
